import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

/** GET /capacity/delivery-quote: when production would start and the earliest dates it can deliver. */
export interface DeliveryQuote {
  production_date: string
  standard_date: string
  /** The earliest delivery on the urgent SLAs; for wording only, it limits nothing. */
  urgent_date: string
  /** Whether every product is offered urgent at all; rush_blocker says why not. */
  rushable: boolean
  rush_blocker: string | null
  /**
   * Every working day from the production start up to the standard date:
   * how many working days it gives, whether the order can be rushed that
   * fast, and each product's rate per piece ("makin cepat makin mahal").
   */
  urgent_by_date: Record<string, UrgentDay>
}

export interface UrgentDay {
  working_days: number
  rushable: boolean
  /** Product id -> RM per piece at this speed and quantity. */
  rates: Record<string, number>
  blocker: string | null
}

/**
 * The quote for an order's lines. Each product's pieces are sent apart, since
 * the rush rate is picked by them. Lines with no pieces are left out.
 */
export function useDeliveryQuote(lines: { productId: number; pieces: number }[], useQuota = true) {
  const counted = lines.filter((line) => line.pieces > 0)

  return useQuery({
    queryKey: ['delivery-quote', counted.map((line) => `${line.productId}x${line.pieces}`).join(','), useQuota],
    queryFn: async () =>
      (
        await api.get<DeliveryQuote>('/capacity/delivery-quote', {
          params: {
            product_ids: counted.map((line) => line.productId),
            quantities: counted.map((line) => line.pieces),
            use_quota: useQuota ? 1 : 0,
          },
        })
      ).data,
    enabled: counted.length > 0,
  })
}

/**
 * What a delivery date means: term for term with
 * DeliveryQuoteService::classify on the server, which re-runs it when the
 * order is placed (client 2026-10-06). Nobody is bound by the SLAs - any day
 * with quota from the production start can be picked, by customers and the
 * team alike, and the server fits the sections into it. Shorter than the
 * standard estimate is urgent when the products can be rushed, otherwise a
 * normal order made for that date. Closed, past and full days are the
 * calendar's to grey out.
 */
export function classifyDelivery(date: string, quote: DeliveryQuote): { urgent: boolean; allowed: boolean } {
  if (date < quote.production_date) return { urgent: false, allowed: false }
  if (date >= quote.standard_date) return { urgent: false, allowed: true }
  return { urgent: Boolean(quote.urgent_by_date[date]?.rushable), allowed: true }
}

/** A product's rush rate per piece for a delivery date, or 0 when the date is not urgent. */
export function urgentRateOn(quote: DeliveryQuote | undefined, date: string | null, productId: number): number {
  if (!quote || !date || !classifyDelivery(date, quote).urgent) return 0
  return quote.urgent_by_date[date]?.rates[String(productId)] ?? 0
}
